import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { purchaseOrdersAPI } from '../api/client';
import TablePagination from '../components/TablePagination';

const STATUS_COLOURS = {
  Draft:      'bg-gray-100 text-gray-700',
  Pending:    'bg-yellow-100 text-yellow-800',
  Approved:   'bg-green-100 text-green-700',
  Open:       'bg-blue-100 text-blue-700',
  Closed:     'bg-slate-100 text-slate-600',
  Cancelled:  'bg-red-100 text-red-700',
  Rejected:   'bg-red-100 text-red-700',
};

const SOURCE_LABELS = {
  internal:     { label: 'Internal', cls: 'bg-indigo-50 text-indigo-700' },
  dynamics_bc:  { label: 'Dynamics BC', cls: 'bg-purple-50 text-purple-700' },
  xero:         { label: 'Xero', cls: 'bg-sky-50 text-sky-700' },
};

const fmtCurrency = (v, currency = 'GBP') =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(v ?? 0);

const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function PurchaseOrderList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initial filters honor URL params (?status=, ?unmatched=1).
  // Status 'Unmatched' is a virtual filter applied client-side.
  const urlStatus = searchParams.get('status') || '';
  const isUnmatched = urlStatus === 'Unmatched' || searchParams.get('unmatched') === '1';
  const [filters, setFilters] = useState({
    status: isUnmatched ? '' : urlStatus,
    search: '',
    source: '',
    dateFrom: '',
    dateTo: '',
    unmatched: isUnmatched,
  });
  const pageSize = 50;

  // React to URL changes (in case TopBar navigation happens while page is mounted)
  useEffect(() => {
    const s = searchParams.get('status') || '';
    const u = s === 'Unmatched' || searchParams.get('unmatched') === '1';
    setFilters(f => ({ ...f, status: u ? '' : s, unmatched: u }));
    setPage(1);
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { unmatched, ...rest } = filters;
      // When viewing unmatched, restrict server-side to open/approved POs (server still returns matched ones in those statuses).
      const params = { page, pageSize, ...rest };
      if (unmatched && !params.status) params.status = 'Open';
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await purchaseOrdersAPI.list(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  // Client-side virtual filter: 'Unmatched' = POs whose MatchedAmount < TotalAmount
  const displayedItems = useMemo(() => {
    if (!filters.unmatched) return items;
    return items.filter(po =>
      ['Approved', 'Open'].includes(po.Status) &&
      (Number(po.MatchedAmount) || 0) < (Number(po.TotalAmount) || 0)
    );
  }, [items, filters.unmatched]);

  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} total records</p>
        </div>
        <button
          onClick={() => navigate('/purchase-orders/create')}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + New PO
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <input
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm col-span-2 md:col-span-1"
          placeholder="Search PO number, vendor…"
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
        />
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          value={filters.unmatched ? 'Unmatched' : filters.status}
          onChange={e => {
            const v = e.target.value;
            if (v === 'Unmatched') {
              setFilters(f => ({ ...f, status: '', unmatched: true }));
            } else {
              setFilters(f => ({ ...f, status: v, unmatched: false }));
            }
            setPage(1);
          }}
        >
          <option value="">All Statuses</option>
          <option value="Unmatched">Unmatched (open with balance)</option>
          {['Draft','Pending','Approved','Open','Closed','Rejected','Cancelled'].map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={filters.source} onChange={e => setFilter('source', e.target.value)}>
          <option value="">All Sources</option>
          <option value="internal">Internal</option>
          <option value="dynamics_bc">Dynamics BC</option>
          <option value="xero">Xero</option>
        </select>
        <input type="date" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={filters.dateFrom} onChange={e => setFilter('dateFrom', e.target.value)} title="Date from" />
        <input type="date" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={filters.dateTo} onChange={e => setFilter('dateTo', e.target.value)} title="Date to" />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {error && <div className="p-4 bg-red-50 text-red-700 text-sm">{error}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['PO Number','Type','Vendor','Status','Source','Total','Matched','Remaining','PO Date','Invoices'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              )}
              {!loading && displayedItems.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No purchase orders found</td></tr>
              )}
              {!loading && displayedItems.map(po => {
                const src = SOURCE_LABELS[po.Source] || { label: po.Source, cls: 'bg-gray-100 text-gray-600' };
                return (
                  <tr key={po.POID} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/purchase-orders/${po.POID}`)}>
                    <td className="px-4 py-3 font-medium text-indigo-700">
                      <Link to={`/purchase-orders/${po.POID}`} className="hover:underline" onClick={e => e.stopPropagation()}>{po.PONumber}</Link>
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-600">{po.POType}</td>
                    <td className="px-4 py-3 text-gray-800">{po.VendorName || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLOURS[po.Status] || 'bg-gray-100 text-gray-600'}`}>
                        {po.Status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${src.cls}`}>{src.label}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-800 font-medium">{fmtCurrency(po.TotalAmount, po.Currency)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtCurrency(po.MatchedAmount, po.Currency)}</td>
                    <td className="px-4 py-3 font-medium text-emerald-700">{fmtCurrency(po.RemainingBalance, po.Currency)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(po.PODate)}</td>
                    <td className="px-4 py-3 text-center">
                      {po.MatchedInvoiceCount > 0
                        ? <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full font-semibold">{po.MatchedInvoiceCount}</span>
                        : <span className="text-gray-300">0</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <TablePagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
      </div>
    </div>
  );
}
